/**
	* Select tool view
	* Handles: selecting, modifying features
	* @module SelectTool
*/

/// Imports

// React
import React, {useState, useEffect} from 'react';

// OpenLayers
import OlMap from 'ol/Map';
import Select from 'ol/interaction/Select';
import Modify from 'ol/interaction/Modify';
import Snap from 'ol/interaction/Snap';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Geometry from 'ol/geom/Geometry';
import Collection from 'ol/Collection';

// Tools
import { assetURL } from 'lib/utilities/asset';
import { getSnapToleranceMultiplier } from 'lib/utilities/map';
import { getToolEnabled } from 'lib/utilities/toolmodes';
import {
	removeFeatureFromSource,
	formatFeature,
	saveFeaturesLocal,
	focusFeatures,
	onFeatureDrawEvent,
	shouldSave,
	getFeatures,
	getFeatureVariation,
	shouldModify
} from 'lib/utilities/feature';

// Lib
import MapToolMode from 'lib/enums/maptoolmode';
import DrawToolInteractions from 'lib/interfaces/drawtoolinteractions';
import DrawEvent from 'lib/enums/drawevent';
import FeatureVariation from 'lib/classes/featurevariation';
import RasterImage from 'lib/classes/rasterimage';
import ChangelogMode from 'lib/enums/changelogmode';

// Popup
import FeatureVariationPopup from 'views/popups/FeatureVariation';
import FeatureEditPopup from 'views/popups/FeatureEdit';

// Lang
import { getLocaleString } from 'lib/localization/vendor';

// Styling
import StyleSelector from 'styles/script/styleselector';
import 'styles/css/DrawTool.scss';

interface SelectToolProps {
	olMap:OlMap,
	currentToolMode:MapToolMode,
}

/*
	Interactions are set in initialize()
*/
const Interactions:DrawToolInteractions = {}; 

/*
	A solid reference to the modify features
*/
const modifyFeatures:Collection<Feature<Geometry>> = new Collection<Feature<Geometry>>();

/*
	Measuring source & layer for measure features
*/
const measuringDrawingSource:VectorSource = new VectorSource();
const measuringLayer:VectorLayer<VectorSource> = new VectorLayer({
	source: measuringDrawingSource
});

export default function SelectTool({olMap, currentToolMode}:SelectToolProps) {
	const mapConfig:any = olMap.get('opus-config');
	
	const [selectedFeatures, setSelectedFeatures] = useState<Feature<Geometry>[]>([]);
	
	/*
		Assign constants from map properties
	*/
	const drawingLayer:VectorLayer<VectorSource> = olMap.get('drawing-layer');
	const measuringLayer:VectorLayer<VectorSource> = olMap.get('measuring-layer');
	const rasterFeatureLayer:VectorLayer<VectorSource> = olMap.get('raster-feature-layer');
	const annotationLayer:VectorLayer<VectorSource> = olMap.get('annotation-layer');
	const drawingSource:VectorSource = drawingLayer.getSource();
	const measuringSource:VectorSource = measuringLayer.getSource();
	const annotationSource:VectorSource = annotationLayer.getSource();
	
	/*
		Map function events
	*/
	const onFeatureFormatted:Function = (f:Feature<Geometry>) => _events.invoke('featureformatted', f);
	const onFeatureSaved:Function = (f:Feature<Geometry>) => _events.invoke('featuresaved', f);
	
	/*
		Initialize
	*/
	useEffect(() => {
		initialize();
	}, []);
	
	/*
		When selection changes
	*/
	useEffect(() => {
		_ui.tooltip.rebuild();
		olMap.set('selected-features', selectedFeatures);
		_events.invoke('selected-features', selectedFeatures);
		_events.on('edit-features-panel', showEditFeaturePanel);
		
		updateModifyFeatures();
		updateSnap();
		
		// Key listener depends on the selectedFeatures state variable being correct
		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUpCallback);
		
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUpCallback);
			
			_events.unall('edit-features-panel');
		}
	}, [selectedFeatures]);
	
	/*
		When tool mode changes
	*/
	useEffect(() => {
		deselectAllFeatures();
		
		const selectEnabled:boolean = (
			currentToolMode == MapToolMode.Select && 
			getToolEnabled(MapToolMode.Select, mapConfig)
		);
		const modifyEnabled:boolean = (
			currentToolMode == MapToolMode.Select &&
			getToolEnabled(MapToolMode.Draw, mapConfig) || getToolEnabled(MapToolMode.Annotation, mapConfig)
		);
		
		Interactions.Select.setActive(selectEnabled);
		Interactions.Modify.setActive(modifyEnabled);
	}, [currentToolMode]);
	
	// Called when map control tool toggled
	useEffect(() => {
		if(!olMap)
			return;
			
		Interactions.Select.setActive(getToolEnabled(MapToolMode.Select, mapConfig));
		
	}, [olMap, getToolEnabled(MapToolMode.Select, mapConfig)]);
	
	useEffect(() => updateSnap(), [currentToolMode]);
	
	/*
		Initialize method
	*/
	const initialize = () => {
		// Setup interactions
		setupInteractions();
		setupMap();
		
		_events.on('remote-features-loaded', () => {
			if(_config.focusFeaturesOnLoad && !olMap.get('url-positioned'))
				focusAllFeatures();
		});
		_events.on('deselect-all-features', () => deselectAllFeatures());
		_events.on('select-features', (features:Feature<Geometry>[]) => selectFeatures(features));
	}
	const setupMap = () => {
		olMap.addInteraction(Interactions.Select);
		olMap.addInteraction(Interactions.Modify);
		olMap.addInteraction(Interactions.Snap);
	}
	const setupInteractions = () => {
		const onModifyEnd = (e:any) => {
			let features:Feature<Geometry>[] = e.features;
			
			features?.forEach((feature:Feature<Geometry>) => {
				feature.unset('inprogress');
				_events.invoke('feature-modify-end', feature);
				
				formatFeature(feature, onFeatureFormatted, ChangelogMode.Modified);
				
				if(shouldSave(feature)) {
					saveFeaturesLocal(drawingSource);
					onFeatureSaved(feature);
					
					/* Callback event */
					onFeatureDrawEvent(DrawEvent.Edit, feature.getGeometry().getType());
				}else{
					feature.set('altered', new Date().getTime());
				}
			});
		}
		
		// Call this event from anywhere to simulate a feature edited event
		_events.on('feature-modify', onModifyEnd);
		
		Interactions.Select = new Select({
			style: StyleSelector,
			layers: (layer:VectorLayer<VectorSource>) => {
				switch(layer) {
					case drawingLayer:
						return getToolEnabled(MapToolMode.Draw, mapConfig);
					case measuringLayer:
						return getToolEnabled(MapToolMode.Measure, mapConfig);
					case annotationLayer:
						return getToolEnabled(MapToolMode.Annotation, mapConfig);
					case rasterFeatureLayer:
						return !olMap.get('ri-locked');
				}
			},
			hitTolerance: 5
		});
		Interactions.Modify = new Modify({
			features: modifyFeatures,
			hitDetection: true
		});
		Interactions.Snap = new Snap({
			features: olMap.get('snap-features'),
			pixelTolerance: 8 * getSnapToleranceMultiplier()
		});
		
		Interactions.Select.on('select', (e:any) => {
			// Remove map marker if one is placed
			_events.invoke('remove-marker');
			
			updateSelectedFeatures();
		});
		Interactions.Modify.on('modifystart', (e:any) => {
			let features:Feature<Geometry>[] = e.features;
				
			const featuresChanged = (features:Feature<Geometry>[]) => {
				features.forEach((feature:Feature<Geometry>) => {
					feature.set('inprogress', true);
					_events.invoke('feature-modify', feature);
					
					formatFeature(feature, onFeatureFormatted);
					
					// None saving features which may need an event handler for propertychange
					if(!shouldSave(feature)) {
						feature.set('altering', new Date().getTime());
					}
				});
			}
			
			clearInterval(Interactions.Modify.get('modify-interval'));
			
			Interactions.Modify.set('modify-interval', setInterval(featuresChanged, 100, features));
		});
		Interactions.Modify.on('modifyend', (e:any) => {
			clearInterval(Interactions.Modify.get('modify-interval'));
			
			onModifyEnd(e);
		});
	}
	
	/**
		Update selected features state
	*/
	const updateSelectedFeatures = () => {
		setSelectedFeatures([...Interactions.Select.getFeatures().getArray()]);
	}
	
	/**
		* Update modify features
		* @remarks
		* Ignore raster image features (non modifiable)
	*/
	const updateModifyFeatures = ():Collection<Feature<Geometry>> => {
		modifyFeatures.clear();
		
		selectedFeatures.forEach((feature:Feature<Geometry>) => {
			if(!shouldModify(feature))
				return;
			
			modifyFeatures.push(feature);
		});
		
		return modifyFeatures;
	}
	
	/*
		Focusses map view on selected features
	*/
	const focusSelectedFeatures = () => {
		if(!getToolEnabled(MapToolMode.MapControl, mapConfig))
			return;
		
		focusFeatures(olMap, selectedFeatures);
	}
	
	/**
		* Remove an existing feature from local storage and the drawing source.
		* 
		* @param {Feature<Geometry>} feature	-	Feature to remove
	*/
	function removeFeature(feature:Feature<Geometry>) {
		// Feature is a raster image tool overlay
		if(feature.get('rasterimg')) {
			_events.invoke('delete-raster-img', feature);
			return;
		}
		
		_events.invoke('remove-feature', feature);
		
		/* Callback event */
		onFeatureDrawEvent(DrawEvent.Delete, feature.getGeometry().getType());
	}
	
	/*
		Removes features that are in `selectedFeatures` from local storage and the drawing source.
	*/
	function removeSelectedFeatures() {
		if(!Interactions.Modify.getActive()) return;
		if(selectedFeatures.length < 1) return;
		
		_ui.showConfirm(getLocaleString('confirmdelete', [String(selectedFeatures.length)]), () => {
			selectedFeatures.forEach((feature:Feature<Geometry>) => {
				removeFeature(feature);
			});
			
			_ui.tooltip.hide();
			
			deselectAllFeatures();
			
			saveFeaturesLocal(drawingSource);
		});
	}
	
	/*
		Select all features and call event to update state hook
	*/
	const selectAllFeatures = () => {
		if(!Interactions.Select.getActive()) return;
		
		const selectedFeatures:Collection<Feature<Geometry>> = Interactions.Select.getFeatures();
		
		const drawnFeatures:Feature<Geometry>[] = getFeatures(drawingSource);
		const measureFeatures:Feature<Geometry>[] = getFeatures(measuringSource);
		const annotationFeatures:Feature<Geometry>[] = getFeatures(annotationSource);
		
		selectedFeatures.clear();
		
		if(getToolEnabled(MapToolMode.Draw, mapConfig))
			selectedFeatures.extend(drawnFeatures);
		
		if(getToolEnabled(MapToolMode.Measure, mapConfig))
			selectedFeatures.extend(measureFeatures);
		
		if(getToolEnabled(MapToolMode.Annotation, mapConfig))
			selectedFeatures.extend(annotationFeatures);
		
		updateSelectedFeatures();
	}
	
	/*
		Focus on all features
	*/
	function focusAllFeatures() {
		focusFeatures(olMap, [...getFeatures(drawingSource), ...getFeatures(measuringSource), ...getFeatures(annotationSource)]);
	}
	
	/*
		Deselect all features and call event to update state hook
	*/
	function deselectAllFeatures() {
		Interactions.Select.getFeatures().clear();
		
		updateSelectedFeatures();
	}
	
	/*
		Select features provided to method
	*/
	function selectFeatures(features:Feature<Geometry>[]) {
		Interactions.Select.getFeatures().extend(features);
		updateSelectedFeatures();
	}
	
	/*
		Get if select tool is enabled
	*/
	const isSelectToolSelected = ():boolean => {
		return currentToolMode == MapToolMode.Select;
	}
	
	/*
		Get active feature variation from selected features
		(if there is one)
	*/
	const getActiveVariationSelected = ():FeatureVariation => {
		let activeVariation:FeatureVariation;
		let match:boolean = true;
		
		selectedFeatures.forEach((feature:Feature<Geometry>) => {
			const variation:FeatureVariation = getFeatureVariation(
				feature.get('variation-id'), 
				feature.get('interaction-name')
			);
			
			if(!activeVariation && variation)
				activeVariation = variation;
			else
			if(activeVariation != variation)
				match = false;
		});
		
		return match ? activeVariation : null;
	}
	
	/*
		Get is the selected features valid for the feature variation tool
	*/
	const isSelectedFeaturesValid = ():boolean => {
		return isSelectedFeaturesSame() && isSelectedFeaturesVariationable();
	}
	
	/*
		Get is the selected features are of the same type or not
	*/
	const isSelectedFeaturesSame = ():boolean => {
		let isSame:boolean = true;
		let type:string = '';
		
		selectedFeatures.forEach((feature:Feature<Geometry>) => {
			if(!type) type = feature.getGeometry().getType();
			
			if(type != feature.getGeometry().getType())
				isSame = false;
		});
		
		return isSame;
	}
	
	/*
		Get is the selected features able to hold variations
	*/
	const isSelectedFeaturesVariationable = ():boolean => {
		let isVariationable:boolean = true;
		
		selectedFeatures.forEach((feature:Feature<Geometry>) => {
			if(feature.get('rasterimg') || feature.get('annotation'))
				isVariationable = false;
		});
		
		return isVariationable;
	}
	
	/*
		Get selected feature type
	*/
	const getSelectedFeatureType = ():string => {
		let isSame:boolean = true;
		let type:string = '';
		
		selectedFeatures.forEach((feature:Feature<Geometry>) => {
			if(!type) type = feature.get('interaction-name');
			
			if(type != feature.get('interaction-name'))
				isSame = false;
		});
		
		return isSame ? type : null;
	}
	
	/*
		Get is raster image selected
	*/
	const getIsRasterImageSelected = ():boolean => {
		let res:boolean = false;
		
		selectedFeatures.forEach((feature:Feature<Geometry>) => {
			if(feature.get('rasterimg'))
				res = true;
		});
		
		return res;
	}
	
	/*
		Key events
	*/
	const handleKeyDown = (e:KeyboardEvent) => {
		// Something like an input box is selected so abort this handler
		if(_ui.hasFocus()) return;
		
		if(e.key == 'Escape' && isSelectToolSelected()) {
			e.preventDefault();
			deselectAllFeatures();
		}
		if(e.key == 'f' && !_ui.isCtrlDown() && !_ui.isShiftDown()) {
			e.preventDefault();
			focusSelectedFeatures();
		}
		
		if(e.key == 'Backspace' && !_ui.isCtrlDown()) {
			e.preventDefault();
			if(isSelectToolSelected())
				removeSelectedFeatures();
		}
		if(_ui.isCtrlDown() && e.key == 'a') {
			e.preventDefault();
			
			_events.invoke('dt-selectall');
			
			setTimeout(() => selectAllFeatures(), 50);
		}
	}
	/*
		Handle key up
	*/
	const handleKeyUpCallback = (e:KeyboardEvent) => {
		// Something like an input box is selected so abort this handler
		if(_ui.hasFocus()) return;
		
		// Key up events (unused)
	}
	
	/*
		Update snap
	*/
	const updateSnap = () => {
		const shouldSnap:boolean = !_ui.isSpaceDown() && !getIsRasterImageSelected() && currentToolMode != MapToolMode.Annotation;
		
		Interactions.Snap.setActive(shouldSnap);
	}
	
	/*
		Show the variations panel to allow user to alter the variation of the feature
	*/
	const showEditVariationPanel = () => {
		const modeName:string = getSelectedFeatureType();
		const featureVariations:FeatureVariation[] = olMap.get('feature-variations')?.get(modeName);
		
		FeatureVariationPopup.set('active-variation', getActiveVariationSelected());
		FeatureVariationPopup.set('variations', featureVariations);
		FeatureVariationPopup.set('mode-name', modeName);
		FeatureVariationPopup.set('onselect', (variation:FeatureVariation) => {
			selectedFeatures.forEach((feature:Feature<Geometry>) => {
				feature.set('variation-id', variation.id);
			});
			_events.invoke('sync-drawtool'); // Save features to server
		});
		
		return _ui.showPopup(FeatureVariationPopup);
	}
	
	/*
		Show the edit feature panel to allow user to alter feature attributes
	*/
	const showEditFeaturePanel = () => {
		FeatureEditPopup.set('features', selectedFeatures);
		FeatureEditPopup.set('map-config', mapConfig);
		
		return _ui.showPopup(FeatureEditPopup);
	}
	
	/*
		Draw the select tool
	*/
	const drawSelectTool = () => {
		if(selectedFeatures.length <= 0) return;
		
		const drawDeleteButton = () => {
			_ui.set('selectTool_deleteButtonShown', true);
			
			return (
				<div 
					className='drawing-option drawing-option--bin' 
					onClick={removeSelectedFeatures} 
					data-tip={`${getLocaleString('delete')} (⌫)`}
				>
					<img src={assetURL('assets/images/bin.png')} />
				</div>
			);
		}
		const drawFocusButton = (deleteButtonDrawn:boolean) => {
			_ui.set('selectTool_focusButtonShown', true);
			
			return (
				<div 
					className={`drawing-option drawing-option--focus ${!deleteButtonDrawn && 'drawing-option--focus-nobin'}`} 
					onClick={focusSelectedFeatures} 
					data-tip={`${getLocaleString('focus')} (F)`}
				>
					<img src={assetURL('assets/images/focus.png')} />
				</div>	
			);
		}
		const drawVariationEditButton = () => {
			_ui.set('selectTool_variationsButtonShown', true);
			
			return (
				<div 
					className='drawing-option drawing-option--variation-edit' 
					onClick={() => showEditVariationPanel()} 
					data-tip={`${getLocaleString('variations')}`}
				>
					<img src={assetURL('assets/images/variations.png')} />
				</div>
			);
		}
		const drawFeatureEditButton = () => {
			_ui.set('selectTool_featureEditButtonShown', true);
			
			return (
				<div 
					className='drawing-option drawing-option--feature-edit' 
					onClick={() => showEditFeaturePanel()} 
					data-tip={`${getLocaleString('metadata')}`}
				>
					<img src={assetURL('assets/images/editdata.png')} />
				</div>
			);
		}
		
		// Reset relevant ui global values
		_ui.set('selectTool_deleteButtonShown', false);
		_ui.set('selectTool_focusButtonShown', false);
		_ui.set('selectTool_featureEditButtonShown', false);
		_ui.set('selectTool_variationsButtonShown', false);
		
		return (
			<>
				{Interactions.Modify.getActive() && drawDeleteButton()}
				{drawFocusButton(Interactions.Modify.getActive())}
				{selectedFeatures.length > 0 && drawFeatureEditButton()}
				{(Interactions.Modify.getActive() && isSelectedFeaturesValid() && _config.featureVariationsEnabled) && drawVariationEditButton()}
			</>
		)
	}
	
	return (
		<>
			{currentToolMode == MapToolMode.Select && drawSelectTool()}
		</>
	)
}